import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface InstitutionAffiliation {
  name: string;
  startYear: number;
  endYear: number;
  rorId: string | null;
}

export interface AnnualActivity {
  year: number;
  works_count: number;
  cited_by_count: number;
}

interface SummaryStats {
  h_index: number;
  i10_index: number;
  works_count: number;
  cited_by_count: number;
}

interface CoAuthor {
  id: string;
  displayName: string;
  institution: string;
}

interface GrowthRate {
  year: number;
  growthPercentage: number;
}

interface CalculatedMetrics {
  avgCitationsPerPublication: number;
  mIndex: number;
  citationsPerYear: number;
  worksGrowthRates: GrowthRate[];
  citationsGrowthRates: GrowthRate[];
  peakPublicationYear?: number;
  peakWorksCount?: number;
  peakCitationYear?: number;
  peakCitedByCount?: number;
  careerLength: number;
}

interface ids {
  orcidId: string | null;
  scopusId: string | null;
}

export interface ResearchTopic {
  display_name: string;
  count: number;
}

export interface MyAnalytics {
  authorName: string;
  openAlexId: string;
  affiliations: InstitutionAffiliation[];
  totalCitations: number;
  totalCount: number;
  countsByYear: AnnualActivity[];
  summaryStats: SummaryStats;
  updateDate: string;
  mostRecentInstitution: string;
  researchTopics: ResearchTopic[];
  ids: ids;
  coAuthors: CoAuthor[];
  calculatedMetrics: CalculatedMetrics;
}

interface AnalyticsContextType {
  myAnalytics: MyAnalytics | null;
  fetchAnalytics: (openAlexId: string) => Promise<void>;
  setMyAnalytics: (data: MyAnalytics | null) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | null>(null);

let cachedAnalytics: MyAnalytics | null = null;

export const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
  const [myAnalytics, setMyAnalytics] = useState<MyAnalytics | null>(cachedAnalytics);
  const { user } = useAuth();

  const fetchAnalytics = async (openAlexId: string) => {
    // Return early if already fetched
    if (cachedAnalytics?.openAlexId === openAlexId) {
      setMyAnalytics(cachedAnalytics);
      return;
    }

    try {
      const response = await fetch(
        `https://academichub-backend.onrender.com/authoranalytics?id=${openAlexId}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch author analytics');
      }

      const analytics = await response.json();
      cachedAnalytics = analytics;
      setMyAnalytics(analytics);
      console.log(analytics);
    } catch (error) {
      console.error('Error fetching author analytics:', error);
    }
  };

  // Fetch once if user logs in with a valid openAlexId
  useEffect(() => {
    if (user?.openAlexId && !cachedAnalytics) {
      fetchAnalytics(user.openAlexId);
    }
  }, [user?.openAlexId]);

  return (
    <AnalyticsContext.Provider value={{ myAnalytics, fetchAnalytics, setMyAnalytics }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
